import { api, QueryParams, RequestOptions } from "services/restUtil";

export type OnSuccess<TData = unknown> = (data: TData) => void;
export type OnError = (error: any) => void;
type RequestQuery = QueryParams | string | null | undefined;
type RequestPayload = object | string | undefined;

function withCallbacks<TResponse>(
  promise: Promise<TResponse>,
  onSuccess?: OnSuccess<TResponse>,
  onError?: OnError
): Promise<TResponse> {
  promise.then(onSuccess, onError);
  return promise;
}

function buildPathWithQuery(path: string, query: RequestQuery): string {
  if (!query) return path;
  if (typeof query === "string") return `${path}?${query}`;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      params.append(key, String(value));
    }
  });
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export function protectedGet<TResponse>(
  path: string,
  query?: RequestQuery,
  onSuccess?: (response: TResponse) => void,
  onError?: OnError,
  config?: RequestOptions
): Promise<TResponse> {
  const fullPath = typeof query === "string" ? buildPathWithQuery(path, query) : path;
  const queryObj = typeof query === "string" ? null : query;
  return withCallbacks(
    api.get<TResponse>(fullPath, queryObj, config),
    onSuccess,
    onError
  );
}

export function protectedPost<TPayload extends RequestPayload, TResponse>(
  path: string,
  payload: TPayload,
  onSuccess?: (response: TResponse) => void,
  onError?: OnError,
  config?: RequestOptions
): Promise<TResponse> {
  return withCallbacks(
    api.post<TPayload, TResponse>(path, payload, config),
    onSuccess,
    onError
  );
}

export function protectedPatch<TPayload extends RequestPayload, TResponse>(
  path: string,
  payload: TPayload,
  onSuccess?: (response: TResponse) => void,
  onError?: OnError
): Promise<TResponse> {
  return withCallbacks(api.patch<TPayload, TResponse>(path, payload), onSuccess, onError);
}

export function protectedPut<TPayload extends RequestPayload, TResponse>(
  path: string,
  data: TPayload,
  onSuccess?: OnSuccess<TResponse>,
  onError?: OnError,
  config?: RequestOptions
): Promise<TResponse> {
  return withCallbacks(
    api.put<TPayload, TResponse>(path, data, config),
    onSuccess,
    onError
  );
}

export function protectedDelete<TResponse>(
  path: string,
  query: RequestQuery,
  onSuccess?: (response: TResponse) => void,
  onError?: OnError
): Promise<TResponse> {
  const fullPath = buildPathWithQuery(path, query);
  return withCallbacks(api.delete<undefined, TResponse>(fullPath), onSuccess, onError);
}

export function protectedDeleteWithBody<TPayload extends RequestPayload, TResponse>(
  path: string,
  payload: TPayload,
  onSuccess?: (response: TResponse) => void,
  onError?: OnError
): Promise<TResponse> {
  return withCallbacks(
    api.delete<TPayload, TResponse>(path, payload),
    onSuccess,
    onError
  );
}
